import { makeStyles } from "tss-react/mui";
import { Grid, Typography, Box, Stack, Button, DialogActions, DialogTitle, DialogContent, TextField, Accordion, AccordionSummary, AccordionDetails, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useStation } from "../../../../providers/StationProvider";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import ResponsiveDialog from "../../../common/core/ResponsiveDialog";

const useStyles = makeStyles()(
  (theme) => ({
    subheader: {
      paddingTop: theme.spacing(1)
    },
    accordion: {
      width: "100%",
      border: `1px solid ${theme.palette.divider}`,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      borderRadius: `${theme.spacing(1)} !important`,
      boxShadow: "none",
      background: "none",
      "& .MuiAccordionDetails-root": {
        padding: 0,
        paddingBottom: theme.spacing(1.5)
      },
      "& .MuiAccordionSummary-root": {
        height: "40px",
        padding: 0,
      },
      "& .MuiAccordionSummary-content": {
        margin: 0,
      }
    },
  })
);


const MAX_LENGTH_160 = { input: { inputProps: { maxLength: 160 } } };

const MAX_LENGTH_16 = { input: { inputProps: { maxLength: 16 } } };

export default function NetworkProfileDialog(props) {
  const { joinNow, task } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { station } = useStation();

  const [description, setDescription] = useState(station?.description || "");
  const [supportPhone, setSupportPhone] = useState(station?.supportPhone || "");
  const [supportEmail, setSupportEmail] = useState(station?.supportEmail || "");
  const [services, setServices] = useState(station?.services || []);

  let allServices = useMemo(() => {
    let svcs = t("assistNetwork:services", { returnObjects: true });
    let categories = Object.keys(svcs);
    let result = [];

    for (let i = 0; i < categories.length; i++) {
      let key = categories[i];
      let val = svcs[key];

      result.push({
        title: val["title"],
        services: Object.keys(val).filter(k => k !== "title").map(k => ({ name: val[k], id: `${key}:${k}` }))
      });
    }

    return result;
  }, [t]);

  const onSubmit = () => {
    task.complete({ services, description, supportEmail, supportPhone });
  };

  return (
    <ResponsiveDialog
      open
      onClose={() => task.throw()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {joinNow ? t("assistNetwork:profileDialog:joinTitle") : t("assistNetwork:profileDialog:title")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={1}>
          <Typography variant="body1" color="textSecondary">{joinNow ? t("assistNetwork:profileDialog:joinDescription") : t("assistNetwork:profileDialog:description")}</Typography>


          <Typography variant="subtitle1" className={classes.subheader}>{t("assistNetwork:profileDialog:aboutStation")}</Typography>

          <TextField
            label={t("assistNetwork:profileDialog:aboutStationDescription")}
            variant="filled"
            value={description}
            onChange={ev => setDescription(ev.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            slotProps={MAX_LENGTH_160}
          />

          <TextField
            label={t("assistNetwork:profileDialog:supportPhone")}
            variant="filled"
            value={supportPhone}
            onChange={ev => setSupportPhone(ev.target.value)}
            fullWidth
            type="tel"
            size="small"
            slotProps={MAX_LENGTH_16}
          />

          <TextField
            label={t("assistNetwork:profileDialog:supportEmail")}
            variant="filled"
            value={supportEmail}
            onChange={ev => setSupportEmail(ev.target.value)}
            slotProps={MAX_LENGTH_160}
            type="email"
            fullWidth
            size="small"
          />

          <Stack>
            <Typography variant="subtitle1" className={classes.subheader}>{t("assistNetwork:profileDialog:services")}</Typography>
            <Typography variant="body2" color="textSecondary">{t("assistNetwork:profileDialog:servicesDescription")}</Typography>
          </Stack>

          <Stack spacing={1.5}>
            {useMemo(() => allServices.map((svc, i) => (
              <ServicesAccordion
                key={i}
                services={services}
                setServices={setServices}
                category={svc}
              />
            )), [services, setServices, allServices])}
          </Stack>

        </Stack>

      </DialogContent>

      <DialogActions>

        <Button onClick={() => task.throw()}>
          {t("common:cancel")}
        </Button>


        <Button variant="contained" color="primary" onClick={onSubmit}>
          {joinNow ? t("assistNetwork:profileDialog:joinAction") : t("common:save")}
        </Button>

      </DialogActions>

    </ResponsiveDialog>
  );
}

function ServicesAccordion(props) {
  const { services, setServices, category, defaultExpanded } = props;
  const { classes } = useStyles();

  let _defaultExpanded = useMemo(() => {
    return !!category.services.find(svc => !!services.find(s => s.id === svc.id));
  }, [category]);

  return (
    <Box>
      <Accordion className={classes.accordion} disableGutters defaultExpanded={_defaultExpanded || defaultExpanded}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography variant="subtitle1">{category.title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            {category.services.map((svc, i) => {
              let selected = !!services.find(s => s.id === svc.id);

              return (
                <Grid item key={i}>
                  <Chip
                    label={svc.name}
                    color={selected ? "primary" : undefined}
                    onClick={() => selected ? setServices(list => list.filter(s => s.id !== svc.id)) : setServices(list => [...list, svc])}
                  />
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}